import { useState } from "react";
import type { ChangeEvent, KeyboardEvent } from "react";

/**
 * Edición local que solo GUARDA al salir del campo (blur) o con Enter.
 *
 * Resuelve el antipatrón de `<input type="date">` / `type="time"`: el input
 * nativo dispara `onChange` en CADA valor válido intermedio mientras navegas
 * el selector (spinner de mes/día/hora). Si guardaras ahí, cada paso pegaría
 * un guardado suelto — bitácora basura, PATCH redundantes y, en fechas de RMA,
 * transiciones intermedias.
 *
 * En vez de eso edita un borrador local y solo llama `onCommit` en blur/Enter,
 * y solo si el valor cambió. El reset cuando el valor del servidor cambia por
 * fuera (otro guardado, recarga) usa el patrón de React "ajustar estado en el
 * render" (guarda el valor previo en estado): sin `useEffect` ni `key` en el
 * padre.
 *
 * Uso:
 *   const campo = useCommitOnBlur(value ?? "", (v) => guardar(v || null));
 *   <input type="date" {...campo} />
 */
export function useCommitOnBlur(value: string, onCommit: (v: string) => void) {
  const [draft, setDraft] = useState(value);
  const [prev, setPrev] = useState(value);
  if (value !== prev) {
    setPrev(value);
    setDraft(value);
  }

  return {
    value: draft,
    onChange: (e: ChangeEvent<HTMLInputElement>) => setDraft(e.target.value),
    onBlur: () => {
      if (draft !== value) onCommit(draft);
    },
    onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") e.currentTarget.blur();
    },
  };
}
